
import { LegacyProvider, LegacyUserStatus, MigrationFailedStep } from '@ld.consumer-portal/models';

export const customerIdentityTenantConfigMock: Record<string, unknown> = {
  success: {
    details: {
      tenantName: 'loanDepot',
      tenantHost: 'localhost',
      ciamDomain: 'ldcorpciamdv',
      entraTenantID: '00000000-0000-0000-0000-000000000001',
      applicationId: '00000000-0000-0000-0000-000000000002',
      isMFAEnabled: true,
      tenantConfiguration: null,
    },
    resultCode: null,
    outcomes: null,
  },
};

export const customerIdentityVerifyEmailMock: Record<string, unknown> = {
  success: {
    details: {
      found: true,
      user: {
        email: 'testuser@loandepot.com',
        firstName: 'Test',
        lastName: 'User',
        externalUserId: '00000000-0000-0000-0000-000000000099',
        entraTenantId: '00000000-0000-0000-0000-000000000001',
        tenantName: 'loanDepot',
        isMFAEnabled: true,
      },
      crossTenantAvailable: false,
      availableTenants: [],
      mfaPhones: [
        { fieldName: 'MFAPhonePrimary', maskedValue: '***-***-4567' },
        { fieldName: 'MFAPhoneAlternate', maskedValue: '***-***-8901' },
      ],
      mfaEmails: [
        { fieldName: 'MFAEmailPrimary', maskedValue: 't***@loandepot.com' },
        { fieldName: 'MFAEmailAlternate', maskedValue: 'j***@gmail.com' },
      ],
      isLegacyUser: false,
    },
    resultCode: null,
    outcomes: null,
  },
  'not-found': {
    details: {
      found: false,
      user: null,
      crossTenantAvailable: false,
      availableTenants: [],
      mfaPhones: [],
      mfaEmails: [],
      isLegacyUser: false,
    },
    resultCode: null,
    outcomes: null,
  },
  'legacy-user': {
    details: {
      found: false,
      user: null,
      crossTenantAvailable: false,
      availableTenants: [],
      mfaPhones: [
        { fieldName: 'MFAPhonePrimary', maskedValue: '***-***-4567' },
      ],
      mfaEmails: [
        { fieldName: 'MFAEmailPrimary', maskedValue: 't***@loandepot.com' },
      ],
      isLegacyUser: true,
      legacyUserStatus: LegacyProvider.MLA,
    },
    resultCode: null,
    outcomes: null,
  },
  'legacy-servicing-user': {
    details: {
      found: false,
      user: null,
      crossTenantAvailable: false,
      availableTenants: [],
      mfaPhones: [],
      mfaEmails: [],
      isLegacyUser: true,
      legacyUserStatus: LegacyProvider.Servicing,
      legacyUserProvider: LegacyProvider.Servicing,
    },
    resultCode: null,
    outcomes: null,
  },
  'no-mfa': {
    details: {
      found: true,
      user: {
        email: 'testuser@loandepot.com',
        firstName: 'Test',
        lastName: 'User',
        externalUserId: '00000000-0000-0000-0000-000000000099',
        entraTenantId: '00000000-0000-0000-0000-000000000001',
        tenantName: 'loanDepot',
        isMFAEnabled: false,
      },
      crossTenantAvailable: false,
      availableTenants: [],
      mfaPhones: [],
      mfaEmails: [],
      isLegacyUser: false,
    },
    resultCode: null,
    outcomes: null,
  },
};

export const customerIdentityCreatePhoneAuthMethodMock: Record<string, unknown> = {
  success: {
    details: {
      id: 'b2d13802-9b55-4aa1-a1c4-12345678abcd',
      phoneNumber: '+17145554567',
      phoneType: 'mobile',
    },
    resultCode: null,
    outcomes: null,
  },
};

export const customerIdentityCreateEmailAuthMethodMock: Record<string, unknown> = {
  success: {
    details: {
      id: 'e3f14903-0c66-5bb2-b2d5-23456789bcde',
      emailAddress: 'testuser@loandepot.com',
    },
    resultCode: null,
    outcomes: null,
  },
};

export const customerIdentityLegacyUserLookupMock: Record<string, unknown> = {
  'mla-user': {
    details: {
      found: true,
      matches: [{ provider: LegacyProvider.MLA, status: LegacyUserStatus.NotMigrated }],
    },
    resultCode: null,
    outcomes: null,
  },
  'b2c-user': {
    details: {
      found: true,
      matches: [{ provider: LegacyProvider.B2C, status: LegacyUserStatus.Active }],
    },
    resultCode: null,
    outcomes: null,
  },
  'b2c-user-flat': {
    details: {
      found: true,
      customerGuid: '70c90ee0-917c-46b5-9984-cb6d46d92f83',
      userStatus: 'B2C',
      isActive: true,
      message: 'Legacy user found via MLA.',
      mfaPhones: { primary: '1111111111', alternate: null, tertiary: null, quaternary: null, quintenary: null },
      emails: [],
    },
    resultCode: null,
    outcomes: null,
  },
  'mla-user-flat': {
    details: {
      found: true,
      customerGuid: '80d01ff1-a28d-57c6-0a95-dc7e57ea3g94',
      userStatus: 'MLA',
      isActive: false,
      message: 'Legacy user found via MLA.',
      mfaPhones: { primary: '2222222222', alternate: null, tertiary: null },
      emails: [],
    },
    resultCode: null,
    outcomes: null,
  },
  'eps-user-flat': {
    details: {
      found: true,
      customerGuid: '11b87291-cc24-426d-a47a-eecd72e651a1',
      userStatus: 'EPS',
      isActive: true,
      message: 'Legacy user found via MLA.',
      mfaPhones: { primary: '7147322032', alternate: null, tertiary: null, quaternary: null, quintenary: null },
      emails: [],
    },
    resultCode: null,
    outcomes: null,
  },
  'not-found': {
    details: {
      found: false,
      matches: [],
      message: 'User not found in any legacy system',
    },
    resultCode: null,
    outcomes: null,
  },
};

export const customerIdentityStartMigrationMock: Record<string, unknown> = {
  success: {
    details: {
      success: true,
      correlationId: 'mock-corr-' + Date.now(),
      ciamUserId: '00000000-0000-0000-0000-000000000099',
      sessionId: 'mock-migration-session-' + Date.now(),
      authorizationUrl:
        'https://login.ciamlogin.com/mock-tenant/oauth2/v2.0/authorize?client_id=mock&redirect_uri=http://localhost:4200/ciam/callback',
      mfaPhones: {
        primary: '+17145554567',
        alternate: '+17145558901',
      },
      message: 'Migration completed successfully',
    },
    resultCode: null,
    outcomes: null,
  },
  'auth-failed': {
    details: {
      success: false,
      failedStep: MigrationFailedStep.LegacyAuthenticate,
      correlationId: 'mock-corr-' + Date.now(),
      message: 'Invalid credentials for legacy provider',
    },
    resultCode: null,
    outcomes: null,
  },
};

export const customerIdentityAuthenticationMethodsMock: Record<string, unknown> = {
  success: {
    details: {
      methods: [
        {
          id: 'b2d13802-9b55-4aa1-a1c4-12345678abcd',
          odataType: '#microsoft.graph.phoneAuthenticationMethod',
          phoneNumber: '+1 (714) ***-4567',
          phoneType: 'mobile',
        },
        {
          id: 'c3e24913-0a66-5bb2-b2d5-23456789bcde',
          odataType: '#microsoft.graph.phoneAuthenticationMethod',
          phoneNumber: '+1 (949) ***-8901',
          phoneType: 'mobile',
        },
        {
          id: 'a1c4d802-9b55-4aa1-b2d1-87654321dcba',
          odataType: '#microsoft.graph.emailAuthenticationMethod',
          emailAddress: 't***@loandepot.com',
        },
        {
          id: 'd4f35a13-1b77-6cc3-c3e6-34567890cdef',
          odataType: '#microsoft.graph.emailAuthenticationMethod',
          emailAddress: 'j***@gmail.com',
        },
      ],
    },
    resultCode: null,
    outcomes: null,
  },
};

export const customerIdentityAuthCallbackMock: Record<string, unknown> = {
  success: {
    details: {
      success: true,
      sessionId: 'mock-migration-session',
      mfaRequired: false,
      authenticationComplete: true,
      user: {
        userId: 12345,
        email: 'testuser@loandepot.com',
        firstName: 'Test',
        lastName: 'User',
      },
      accessToken: 'mock-migration-access-token',
      idToken: 'mock-migration-id-token',
      expiresIn: 3600,
      tokenType: 'Bearer',
    },
    resultCode: null,
    outcomes: null,
  },
};

export const customerIdentityNativeSignupStartMock: Record<string, unknown> = {
  success: {
    details: {
      continuationToken: 'mock-continuation-token-start-' + Date.now(),
      codeLength: 6,
      interval: 300,
      challengeChannel: 'email',
    },
    resultCode: null,
    outcomes: null,
  },
};

export const customerIdentityCreateUserProfilePropertiesMock: Record<string, unknown> = {
  success: {
    details: {
      success: true,
      invalidProperties: null,
    },
    resultCode: null,
    outcomes: null,
  },
};

export const customerIdentityNativeSignupContinueMock: Record<string, Record<string, unknown>> = {
  'otp-verified': {
    details: {
      continuationToken: 'mock-continuation-token-otp-' + Date.now(),
      status: 'password_required',
    },
    resultCode: null,
    outcomes: null,
  },
  'password-set': {
    details: {
      continuationToken: 'mock-continuation-token-password-' + Date.now(),
      status: 'attributes_required',
    },
    resultCode: null,
    outcomes: null,
  },
  complete: {
    details: {
      status: 'succeeded',
      userId: '00000000-0000-0000-0000-000000000456',
      accessToken: 'mock-signup-access-token',
      idToken: 'mock-signup-id-token',
      expiresIn: 3600,
    },
    resultCode: null,
    outcomes: null,
  },
};
